/* eslint-disable no-mixed-operators */
import * as React from 'react';
import { connect } from "react-redux";
import { TextField } from '@fluentui/react/lib/TextField';
import { IconButton } from '@fluentui/react/lib/Button';
import { DirectionalHint, Icon, Link, Stack,TooltipHost } from '@fluentui/react';
import * as signalR from "@microsoft/signalr";
import RbLoadingSpinner from '../../../bosch-react/components/loading-spinner/RbLoadingSpinner';
import { ChatModes, DocumentRef, IMessage, SearchType } from '../types';
import { getAllMessages, saveMessage } from '../../../services/indexedDBServices';
import { RootState } from '../../../store/store';
import { IUserProfile } from '../../Auth/types';
import { removeAliasDomain } from '../../../utils/converter';
import MessageContent from './MessageContent';
import { SIGNALR_METHODS, CHAT_UI, TIMEOUTS, COLORS, MESSAGES, ICONS } from './constants';

type ChatModeOption = 'normal' | 'advanced' | 'pep';

interface State {
    isOpen: boolean;
    messages: IMessage[];
    currentInput: string;
    currentRefs: DocumentRef[];
    currentStreamMessage: string;
    waiting: boolean;
    isStreaming: boolean;
    shouldAutoScroll: boolean; // Track if auto-scroll should happen
    showScrollToBottom: boolean;
    selectedChatMode: ChatModeOption;
}

interface IProps {
    userProfile: IUserProfile | undefined;
}

const chatModeDescriptions: Record<string, string> = {
    normal: 'Retrieves information from Lessons Learned documents.',
    advanced: 'Retrieves information from all available document repositories (excluded SC3).',
    pep: 'Retrieves information from the PEP document index.',
};

class ChatWindow extends React.Component<IProps, State> {
    private connection: signalR.HubConnection | undefined;
    private getCurrentTime = () => {
        return new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    private messageEndRef = React.createRef<HTMLDivElement>();
    private messagesContainerRef = React.createRef<HTMLDivElement>(); // Add ref for messages container
    private completeAfterStream: boolean = false;
    private tokenQueue: string[] = [];
    private streamInterval: NodeJS.Timeout | null = null;

    state: State = {
        isOpen: false,
        messages: [{
            content: MESSAGES.WELCOME,
            time: this.getCurrentTime(),
            isMe: false,
            searchType: SearchType.LLSearch
        }],
        currentInput: '',
        currentRefs: [],
        currentStreamMessage: "",
        waiting: false,
        isStreaming: false,
        shouldAutoScroll: true, // Initially true
        showScrollToBottom: false,
        selectedChatMode: 'normal',
    };

    // Manual scroll to bottom when button is clicked
    private scrollToBottomManually = () => {
        this.setState({
            shouldAutoScroll: true,
            showScrollToBottom: false
        }, () => {
            this.scrollToBottom();
        });
    };

    // Check if user is at the bottom of the chat
    private isUserAtBottom = (): boolean => {
        if (!this.messagesContainerRef.current) return true;

        const container = this.messagesContainerRef.current;
        const threshold = 100; // 100px threshold for "near bottom"
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;

        return isAtBottom;
    };

    determineSearchTypes = () => {
        const { userProfile } = this.props;
        const searchType = userProfile?.menuFeatures?.find((feature) => feature.featureName === ChatModes.ADVANCED_CHAT && feature.hasAccess)
            ? SearchType.NonSC3Search
            : SearchType.LLSearch;

        console.log('[ChatWindow] determineSearchTypes', {
            value: searchType,
            label: SearchType[searchType]
        });

        return searchType;
    }

    getSearchTypeForMode = (): SearchType => {
        switch (this.state.selectedChatMode) {
            case 'advanced': return SearchType.NonSC3Search;
            case 'pep': return SearchType.PepSearch;
            default: return SearchType.LLSearch;
        }
    }

    async componentDidMount() {
        const { userProfile } = this.props;
        if (!userProfile) return;

        const history = await getAllMessages(removeAliasDomain(userProfile.loginName));
        if (history && history.length > 0) {
            this.setState({
                messages: [...this.state.messages, ...history]
            });
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_FEBER_API_BASE_URL}chathub`)
            .withAutomaticReconnect()
            .build();

        this.connection.serverTimeoutInMilliseconds = TIMEOUTS.SERVER_TIMEOUT;
        this.connection.keepAliveIntervalInMilliseconds = TIMEOUTS.KEEP_ALIVE_INTERVAL;

        this.connection.on(SIGNALR_METHODS.RECEIVE_REFERENCES, (references: DocumentRef[]) => {
            this.setState({ currentRefs: references })
        });

        this.connection.on(SIGNALR_METHODS.RECEIVE_TOKEN, (token: string) => {
            this.enqueueStreamToken(token);
        });

        this.connection.on(SIGNALR_METHODS.STREAM_STARTED, () => {
            this.setState({ isStreaming: true, waiting: false });
        });

        this.connection.on(SIGNALR_METHODS.STREAM_CANCELLED, () => {
            this.handleStreamCancellation();
        });

        this.connection.on(SIGNALR_METHODS.COMPLETE, () => {
            const final = this.state.currentStreamMessage;
            if (final.trim()) {
                const newChat = {
                    content: final,
                    time: this.getCurrentTime(),
                    isMe: false,
                    searchType: this.getSearchTypeForMode(),
                    refs: this.state.currentRefs
                } as IMessage;

                this.setState(prev => ({
                    messages: [...prev.messages, newChat],
                    currentStreamMessage: "",
                    currentRefs: [],
                    waiting: false,
                    isStreaming: false,
                }), () => {
                    saveMessage(removeAliasDomain(this.props.userProfile!.loginName), newChat);
                });
            } else {
                this.setState({
                    currentStreamMessage: "",
                    currentRefs: [],
                    waiting: false,
                    isStreaming: false,
                });
            }
        });

        this.connection.start();
    }

    handleStreamCancellation = () => {
        console.log("Stream cancelled by server.");

        if (this.streamInterval) {
            clearInterval(this.streamInterval);
            this.streamInterval = null;
        }

        if (this.state.currentStreamMessage.trim()) {
            const partialMessage: IMessage = {
                content: this.state.currentStreamMessage + MESSAGES.CANCELLED_SUFFIX,
                time: this.getCurrentTime(),
                isMe: false,
                searchType: this.getSearchTypeForMode(),
                refs: this.state.currentRefs
            };

            this.setState(prev => ({
                messages: [...prev.messages, partialMessage],
                currentStreamMessage: "",
                currentRefs: [],
                waiting: false,
                isStreaming: false,
            }), () => {
                saveMessage(removeAliasDomain(this.props.userProfile!.loginName), partialMessage);
            });
        } else {
            this.setState({
                currentStreamMessage: "",
                currentRefs: [],
                waiting: false,
                isStreaming: false,
            });
        }

        this.completeAfterStream = false;
    };

    stopStreaming = async () => {
        if (this.connection) {
            try {
                console.log("Stopping stream...");
                await this.connection.invoke(SIGNALR_METHODS.STOP_CHAT_STREAM);
            } catch (error) {
                console.error("Error stopping stream:", error);
                this.handleStreamCancellation();
            }
        }
    };

    enqueueStreamToken = (token: string) => {
        this.setState(prev => ({
            currentStreamMessage: prev.currentStreamMessage + token
        }));
    };

    private handleScroll = () => {
        const isAtBottom = this.isUserAtBottom();

        // Update both shouldAutoScroll and showScrollToBottom
        this.setState({
            shouldAutoScroll: isAtBottom,
            showScrollToBottom: !isAtBottom  // Show button when NOT at bottom
        });
    };

    componentDidUpdate(prevProps: IProps, prevState: State) {
        // Auto-scroll when chat opens
        if (!prevState.isOpen && this.state.isOpen) {
            // Reset auto-scroll to true when chat opens
            this.setState({ shouldAutoScroll: true }, () => {
                setTimeout(() => {
                    this.scrollToBottom();
                }, 100);
            });
        }

        // Auto-scroll only if user is at bottom and there are new messages
        if (prevState.messages.length !== this.state.messages.length && this.state.shouldAutoScroll) {
            setTimeout(() => {
                this.scrollToBottom();
            }, 50);
        }

        // Auto-scroll during streaming only if user is at bottom
        if (prevState.currentStreamMessage !== this.state.currentStreamMessage && this.state.shouldAutoScroll) {
            setTimeout(() => {
                this.scrollToBottom();
            }, 50);
        }
    }

    togglePanel = () => {
        this.setState({ isOpen: !this.state.isOpen });
    };

    scrollToBottom = () => {
        if (this.messageEndRef.current) {
            this.messageEndRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end'
            });
        }
    };

    sendOrStopMessage = async () => {
        const { currentInput, isStreaming } = this.state;

        if (isStreaming) {
            await this.stopStreaming();
            return;
        }

        if (!currentInput.trim() || !this.connection) return;

        this.setState({
            currentStreamMessage: "",
            waiting: true,
            currentInput: "",
            isStreaming: false,
            currentRefs: [],
            shouldAutoScroll: true, // Reset to true when sending message
            showScrollToBottom: false, // Hide button when sending message
        });

        const newMessage: IMessage = {
            content: currentInput,
            time: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
            isMe: true,
            searchType: this.getSearchTypeForMode(),
        };

        this.setState(prev => ({
            messages: [...prev.messages, newMessage],
        }), async () => {
            saveMessage(removeAliasDomain(this.props.userProfile!.loginName), newMessage);
            const messages = this.state.messages.slice(-10);
            await this.connection!.invoke(SIGNALR_METHODS.CHAT_WITH_RAG, messages);
        });
    };

    handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.sendOrStopMessage();
        }
    };

    isMultipleLine = () => this.state.currentInput.length > CHAT_UI.MULTI_LINE_THRESHOLD;

    onRenderNavigation = () => {
        return (
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{
                padding: '0 16px',
                backgroundColor: COLORS.PRIMARY,
                marginBottom: 16,
                height: CHAT_UI.HEADER_HEIGHT
            }}>
                <span style={{ fontWeight: 800, color: 'white' }}>AI Assistant</span>
                <div>
                    <IconButton
                        iconProps={{ iconName: ICONS.CANCEL }}
                        title="Close"
                        onClick={this.togglePanel}
                        styles={{
                            root: { color: 'white' },
                            rootHovered: { background: 'rgba(255,255,255,0.1)' },
                        }}
                    />
                </div>
            </Stack>
        )
    }

    onRenderMiniChat = () => {
        const { messages, currentInput, currentStreamMessage, waiting, isStreaming, showScrollToBottom, selectedChatMode } = this.state;
        const { userProfile } = this.props;

        const hasAccess = (mode: ChatModes): boolean =>
            userProfile?.menuFeatures?.find(f => f.featureName === mode)?.hasAccess === true;

        const chatModeIcons: Record<ChatModeOption, string> = {
            normal: 'Chat',
            advanced: 'Rocket',
            pep: 'People',
        };
        return (
            <div style={{
                position: 'fixed',
                bottom: 70,
                right: 32,
                width: CHAT_UI.WINDOW_WIDTH,
                height: CHAT_UI.WINDOW_HEIGHT,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                zIndex: 1300
            }}>
                {this.onRenderNavigation()}

                <div
                    ref={this.messagesContainerRef}
                    onScroll={this.handleScroll}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 10,
                        backgroundColor: '#fff'
                    }}
                >
                    {messages.map((msg, idx) => {
                        const isMe = msg.isMe;
                        return (
                            <div key={idx} style={{
                                marginBottom: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isMe ? 'flex-end' : 'flex-start'
                            }}>
                                <div style={{
                                    fontFamily: 'BoschFont',
                                    background: isMe ? COLORS.PRIMARY : COLORS.BACKGROUND_LIGHT,
                                    color: isMe ? 'white' : 'black',
                                    padding: 10,
                                    borderRadius: 12,
                                    maxWidth: CHAT_UI.MESSAGE_MAX_WIDTH,
                                    textAlign: 'left'
                                }}>
                                    <MessageContent content={msg.content} isMe={isMe} />
                                </div>

                                {!isMe && msg.refs && msg.refs.length > 0 && (
                                    <div style={{
                                        marginTop: 8,
                                        maxWidth: CHAT_UI.MESSAGE_MAX_WIDTH,
                                        fontSize: 12
                                    }}>
                                        <div style={{
                                            color: COLORS.TEXT_GRAY,
                                            fontWeight: 'bold',
                                            marginBottom: 4
                                        }}>
                                            {MESSAGES.REFERENCES_TITLE}
                                        </div>
                                        {msg.refs.map((ref, refIdx) => (
                                            <div key={refIdx} style={{
                                                background: COLORS.REFERENCE_BG,
                                                border: `1px solid ${COLORS.REFERENCE_BORDER}`,
                                                borderRadius: 6,
                                                padding: 6,
                                                marginBottom: 4,
                                                fontSize: 11
                                            }}>
                                                <Link
                                                    target='_blank'
                                                    href={ref.accessMediatorUrl}
                                                    style={{
                                                        fontWeight: 'bold',
                                                        color: '#495057',
                                                        marginBottom: 2
                                                    }}>
                                                    {ref.title}
                                                </Link>
                                                <div style={{
                                                    color: COLORS.TEXT_GRAY,
                                                    fontStyle: 'italic',
                                                    textDecoration: 'none',
                                                }}>
                                                    "{ref.highLights}"
                                                </div>
                                                <div style={{
                                                    color: COLORS.SUCCESS,
                                                    fontSize: 10,
                                                    marginTop: 2
                                                }}>
                                                    Score: {((ref.rerankerScore / 4.0) * 10.0).toFixed(1)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <span style={{ fontSize: 10, color: COLORS.TEXT_GRAY, marginTop: 2 }}>{msg.time}</span>
                            </div>
                        )
                    })}

                    {currentStreamMessage && (
                        <div style={{
                            marginBottom: 12,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start'
                        }}>
                            <div style={{
                                fontFamily: 'BoschFont',
                                background: COLORS.BACKGROUND_LIGHT,
                                color: 'black',
                                padding: 10,
                                borderRadius: 12,
                                maxWidth: CHAT_UI.MESSAGE_MAX_WIDTH,
                                textAlign: 'left',
                                position: 'relative'
                            }}>
                                <MessageContent content={currentStreamMessage} isMe={false} />

                                {isStreaming && (
                                    <div style={{
                                        marginTop: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <IconButton
                                            iconProps={{ iconName: ICONS.MORE }}
                                            title="Gennerating..."
                                            styles={{
                                                root: {
                                                    minWidth: 24,
                                                    width: 24,
                                                    height: 24,
                                                    color: COLORS.PRIMARY
                                                },
                                            }}
                                        />
                                        <span style={{ fontSize: 11, color: COLORS.TEXT_GRAY }}>
                                            {MESSAGES.GENERATING}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isStreaming && <div style={{ textAlign: 'center', marginTop: 20 }}><RbLoadingSpinner size={0.5} /></div>}
                    <div ref={this.messageEndRef} />
                    {/* Scroll to bottom button */}
                    {showScrollToBottom && (
                        <div style={{
                            position: 'absolute',
                            bottom: 60,
                            right: 16,
                            zIndex: 10
                        }}>
                            <IconButton
                                iconProps={{ iconName: 'ChevronDown' }}
                                title="Scroll to bottom"
                                onClick={this.scrollToBottomManually}
                                styles={{
                                    root: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        color: 'white',
                                        border: 'none',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                        transition: 'all 0.2s ease'
                                    },
                                    rootHovered: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        transform: 'scale(1.1)'
                                    },
                                    rootPressed: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                        transform: 'scale(0.95)'
                                    },
                                    icon: {
                                        fontSize: 14
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="ms-Grid" style={{
                    borderTop: `1px solid ${COLORS.BORDER_GRAY}`,
                    backgroundColor: COLORS.BACKGROUND_GRAY
                }}>
                    <div className="ms-Grid-row" style={{ position: 'relative' }}>
                        <div className="ms-sm12">
                            <TextField
                                placeholder={MESSAGES.PLACEHOLDER}
                                value={currentInput}
                                multiline={this.isMultipleLine()}
                                autoAdjustHeight={this.isMultipleLine()}
                                borderless
                                autoFocus
                                resizable={false}
                                disabled={waiting}
                                onChange={(_, val) => this.setState({ currentInput: val || '' })}
                                onKeyDown={this.handleKeyDown}
                                styles={{
                                    fieldGroup: {
                                        fontSize: '14px !important',
                                        height: 'auto !important'
                                    },
                                    field: {
                                        fontSize: '14px !important',
                                        paddingRight: '88px !important',
                                        height: CHAT_UI.INPUT_HEIGHT,
                                        maxHeight: CHAT_UI.INPUT_MAX_HEIGHT,
                                        overflowY: 'auto',
                                    },
                                }}
                            />
                        </div>
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            right: '40px',
                            transform: 'translateY(-50%)',
                            marginRight: 4,
                        }}>
                            <IconButton
                                    iconProps={{ iconName: chatModeIcons[selectedChatMode] }}
                                    menuIconProps={{ iconName: 'ChevronDown' }}
                                    title={!selectedChatMode ? chatModeDescriptions['normal'] : chatModeDescriptions[selectedChatMode]}
                                    disabled={isStreaming || waiting}
                                    style={{
                                        width: 36,
                                        height: 36,
                                        color: COLORS.PRIMARY,
                                        fontSize: 11,
                                    }}
                                    menuProps={{
                                        items: [
                                            ...(hasAccess(ChatModes.STANDARD_CHAT)
                                                ? [{
                                                    key: 'normal',
                                                    text: 'Normal Chat',
                                                    title: chatModeDescriptions['normal'],
                                                    iconProps: { iconName: 'Chat' },
                                                    canCheck: true,
                                                    isChecked: selectedChatMode === 'normal',
                                                    onClick: () => this.setState({ selectedChatMode: 'normal' }),
                                                }]
                                                : []),
                                            ...(hasAccess(ChatModes.ADVANCED_CHAT)
                                                ? [{
                                                    key: 'advanced',
                                                    text: 'Advanced Chat',
                                                    iconProps: { iconName: 'Rocket' },
                                                    title: chatModeDescriptions['advanced'],
                                                    canCheck: true,
                                                    isChecked: selectedChatMode === 'advanced',
                                                    onClick: () => this.setState({ selectedChatMode: 'advanced' }),
                                                }]
                                                : []),
                                            ...(hasAccess(ChatModes.PEP_CHAT)
                                                ? [{
                                                    key: 'pep',
                                                    text: 'PEP Chat',
                                                    iconProps: { iconName: 'People' },
                                                    title: chatModeDescriptions['pep'],
                                                    canCheck: true,
                                                    isChecked: selectedChatMode === 'pep',
                                                    onClick: () => this.setState({ selectedChatMode: 'pep' }),
                                                }]
                                                : []),
                                        ],
                                    }}
                                />
                        </div>
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            right: '0',
                            transform: 'translateY(-50%)',
                            marginRight: 8,
                        }}>
                            <IconButton
                                iconProps={{
                                    iconName: isStreaming || waiting ? ICONS.STOP : ICONS.SEND
                                }}
                                title={isStreaming ? "Cancel generating" : "Send message"}
                                style={{
                                    width: CHAT_UI.BUTTON_SIZE,
                                    height: CHAT_UI.BUTTON_SIZE,
                                }}
                                onClick={this.sendOrStopMessage}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { isOpen } = this.state;
        return (
            <>
                <ChatButton onClick={this.togglePanel} />
                {isOpen && this.onRenderMiniChat() || null}
            </>
        );
    }
}

const ChatButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <div className="chat-bubble-wrapper" style={{ position: 'fixed', bottom: 70, right: 32, zIndex: 1300 }}>
        <IconButton
            className="chat-bubble-btn"
            style={{
                borderRadius: '50%',
                backgroundColor: COLORS.PRIMARY,
                color: 'white',
                height: CHAT_UI.CHAT_BUTTON_SIZE,
                width: CHAT_UI.CHAT_BUTTON_SIZE,
                lineHeight: `${CHAT_UI.CHAT_BUTTON_SIZE}px`
            }}
            onClick={onClick}
        >
            <Icon iconName="Chat" style={{ fontSize: 22 }} />
        </IconButton>
    </div>
);

const mapStateToProps = (state: RootState) => ({
    userProfile: state.auth.userProfile,
});

export default connect(mapStateToProps, {})(ChatWindow);